import React from 'react';
import {
  Card,
  Tag,
  Badge,
  List,
  Typography,
  Empty,
  Spin,
  Divider,
  Tooltip,
} from 'antd';
import {
  CarOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  WifiOutlined,
  DisconnectOutlined,
  BarChartOutlined,
  UnorderedListOutlined,
  UserOutlined,
  TagOutlined,
} from '@ant-design/icons';
import { Bar } from 'react-chartjs-2';
import Cookies from 'js-cookie';
import Config from '../Config';
import { apiClient } from '../store/store';

const { Text } = Typography;
const config = new Config();

const CHART_OPTIONS = {
  responsive: true,
  maintainAspectRatio: true,
  legend: { display: false },
  animation: { duration: 300 },
  scales: {
    yAxes: [{
      ticks: { beginAtZero: true, precision: 0 },
      scaleLabel: { display: true, labelString: 'Kendaraan' },
    }],
    xAxes: [{
      ticks: { fontSize: 10, autoSkip: false, maxRotation: 0 },
    }],
  },
  tooltips: {
    callbacks: {
      label: item => `${item.yLabel} kendaraan`,
    },
  },
};

function buildChartData(jamData) {
  const currentHour = new Date().getHours();
  const labels = [];
  const values = [];
  for (let h = 0; h < 24; h++) {
    const key = String(h).padStart(2, '0');
    labels.push(h % 2 === 0 ? `${key}:00` : '');
    values.push(jamData[key] || 0);
  }
  return {
    labels,
    datasets: [{
      label: 'Pembayaran per Jam',
      data: values,
      backgroundColor: values.map((_, i) =>
        i === currentHour ? 'rgba(82,196,26,0.85)' : 'rgba(24,144,255,0.55)'
      ),
      borderColor: values.map((_, i) =>
        i === currentHour ? '#52c41a' : '#1890ff'
      ),
      borderWidth: 1,
      borderRadius: 3,
    }],
  };
}

class RealtimePenerimaan extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      uptNama: '',
      tanggal: '',
      listNopol: [],
      total: 0,
      wsConnected: false,
      lastUpdate: null,
      chartData: buildChartData({}),
      newItemId: null,
      wsGroup: null,
      tampilkanUpt: false,
    };
    this.ws = null;
    this.jamData = {};
    this.reconnectTimer = null;
  }

  componentDidMount() {
    this._init();
  }

  componentDidUpdate(prevProps) {
    const changed =
      prevProps.uptDipilih !== this.props.uptDipilih ||
      prevProps.uptParam !== this.props.uptParam ||
      prevProps.kabupatenParam !== this.props.kabupatenParam;
    if (changed) {
      this._cleanup();
      this.jamData = {};
      this.setState({ listNopol: [], total: 0, wsConnected: false, lastUpdate: null });
      this._init();
    }
  }

  componentWillUnmount() {
    this._cleanup();
  }

  _getUptId() {
    try {
      const raw = this.props.uptDipilih;
      if (!raw) return null;
      const obj = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return obj.id || null;
    } catch {
      return null;
    }
  }

  _cleanup() {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    if (this.ws) {
      this.ws.onclose = null;
      this.ws.close();
      this.ws = null;
    }
  }

  _init() {
    this.setState({ loading: true });
    this._fetchInitial();
  }

  async _fetchInitial() {
    try {
      const token = Cookies.get(config.get_cookie_name());
      const { uptParam, kabupatenParam } = this.props;
      const uptId = this._getUptId();
      const params = new URLSearchParams();
      if (uptParam)            params.set('upt_id', uptParam);
      else if (kabupatenParam) params.set('kabupaten_id', kabupatenParam);
      else if (uptId)          params.set('upt_id', uptId);
      const qs = params.toString() ? `?${params.toString()}` : '';

      const response = await apiClient.get(
        `transaksi/api/penerimaan-realtime/${qs}`,
        { headers: { Authorization: token || '' } }
      );
      const data = response.data;
      this.jamData = {};
      (data.chart_per_jam || []).forEach(d => { this.jamData[d.jam] = d.jumlah; });
      const wsGroup = data.ws_group || null;
      this.setState({
        uptNama: data.upt_nama || '',
        tanggal: data.tanggal || '',
        total: data.total || 0,
        listNopol: data.list_nopol || [],
        chartData: buildChartData(this.jamData),
        wsGroup,
        tampilkanUpt: !!data.tampilkan_upt,
        loading: false,
      }, () => { if (wsGroup) this._connectWS(wsGroup); });
    } catch (err) {
      console.error('RealtimePenerimaan fetch error:', err.response || err.message);
      this.setState({ loading: false });
    }
  }

  _connectWS(wsGroup) {
    const wsUrl = `${config.get_web_socket()}/ws/penerimaan/${wsGroup}/`;
    this.ws = new WebSocket(wsUrl);

    this.ws.onopen = () => this.setState({ wsConnected: true });

    this.ws.onmessage = (e) => {
      try {
        const item = JSON.parse(e.data);
        // Notifikasi parent untuk semua jenis event (pendaftaran/penetapan/pembayaran)
        if (this.props.onNewTransaction) {
          this.props.onNewTransaction(item);
        }
        // Hanya tambahkan ke list dan chart jika event pembayaran
        if (item.type === 'pembayaran' || !item.type) {
          this.jamData[item.jam] = (this.jamData[item.jam] || 0) + 1;
          this.setState(prev => ({
            listNopol: [item, ...prev.listNopol].slice(0, 300),
            total: prev.total + 1,
            chartData: buildChartData(this.jamData),
            lastUpdate: new Date(),
            newItemId: item.id,
          }));
          setTimeout(() => this.setState({ newItemId: null }), 2000);
        }
      } catch { /* ignore malformed */ }
    };

    this.ws.onclose = () => {
      this.setState({ wsConnected: false });
      // Reconnect otomatis setelah 5 detik
      this.reconnectTimer = setTimeout(() => {
        const { wsGroup } = this.state;
        if (wsGroup) this._connectWS(wsGroup);
      }, 5000);
    };
  }

  render() {
    const {
      loading, uptNama, tanggal, listNopol, total,
      wsConnected, lastUpdate, chartData, newItemId, tampilkanUpt,
    } = this.state;

    const { wsGroup } = this.state;
    const tidakAdaScope = !loading && !wsGroup;

    return (
      <Card
        style={{
          borderRadius: 10,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          marginTop: 16,
          border: wsConnected ? '1px solid #b7eb8f' : '1px solid #f0f0f0',
          transition: 'border-color 0.4s ease',
        }}
        bodyStyle={{ padding: '14px 18px' }}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <BarChartOutlined style={{ color: '#1890ff', fontSize: 17 }} />
            <span style={{ fontWeight: 700, fontSize: 14 }}>Penerimaan Harian Realtime</span>
            {uptNama && (
              <Tag color="blue" style={{ borderRadius: 20, fontSize: 11, margin: 0 }}>
                {uptNama}
              </Tag>
            )}
            {tanggal && (
              <Tag color="default" style={{ borderRadius: 20, fontSize: 11, margin: 0 }}>
                <ClockCircleOutlined style={{ marginRight: 3 }} />
                {tanggal}
              </Tag>
            )}
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
              <Tooltip title="Total pembayaran hari ini">
                <Badge
                  count={total}
                  showZero
                  overflowCount={9999}
                  style={{ backgroundColor: '#52c41a', cursor: 'default' }}
                />
              </Tooltip>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                {wsConnected
                  ? <WifiOutlined style={{ color: '#52c41a', fontSize: 14 }} />
                  : <DisconnectOutlined style={{ color: '#ff4d4f', fontSize: 14 }} />
                }
                <Text style={{
                  fontSize: 11,
                  color: wsConnected ? '#52c41a' : '#ff4d4f',
                  fontWeight: 600,
                }}>
                  {wsConnected ? 'Live' : 'Terputus'}
                </Text>
                {wsConnected && (
                  <div style={{
                    width: 7, height: 7, borderRadius: '50%',
                    backgroundColor: '#52c41a',
                    animation: 'penerimaan-blink 1.4s ease-in-out infinite',
                  }} />
                )}
              </div>
            </div>
          </div>
        }
      >
        {tidakAdaScope ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Text type="secondary" style={{ fontSize: 13 }}>
                Pilih UPT terlebih dahulu untuk melihat penerimaan realtime
              </Text>
            }
            style={{ padding: '24px 0' }}
          />
        ) : (
          <Spin spinning={loading}>
            {/* ── Grafik per Jam ── */}
            <div style={{ marginBottom: 4 }}>
              <Text type="secondary" style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 4, marginBottom: 6 }}>
                <BarChartOutlined />
                Distribusi pembayaran per jam hari ini
                <span style={{
                  display: 'inline-block', width: 10, height: 10,
                  borderRadius: 2, background: 'rgba(82,196,26,0.85)',
                  marginLeft: 8, verticalAlign: 'middle',
                }} />
                <span style={{ fontSize: 10 }}>jam sekarang</span>
              </Text>
              <Bar data={chartData} options={CHART_OPTIONS} height={80} />
            </div>

            <Divider style={{ margin: '12px 0' }} />

            {/* ── List Nomor Polisi ── */}
            <div style={{ marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
              <UnorderedListOutlined style={{ color: '#1890ff', fontSize: 13 }} />
              <Text strong style={{ fontSize: 12 }}>
                Daftar Pembayaran Hari Ini
              </Text>
              <Tag style={{ borderRadius: 20, fontSize: 10, marginLeft: 4 }}>
                {total} data
              </Tag>
            </div>

            <div
              style={{
                maxHeight: 380,
                overflowY: 'auto',
                border: '1px solid #f0f0f0',
                borderRadius: 8,
                background: '#fafafa',
              }}
            >
              {listNopol.length === 0 ? (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="Belum ada pembayaran hari ini"
                  style={{ padding: '28px 0' }}
                />
              ) : (
                <List
                  size="small"
                  dataSource={listNopol}
                  renderItem={(item) => (
                    <List.Item
                      key={item.id}
                      style={{
                        padding: '5px 12px',
                        background: item.id === newItemId
                          ? 'rgba(82,196,26,0.12)'
                          : 'transparent',
                        transition: 'background 0.6s ease',
                        borderBottom: '1px solid #f0f0f0',
                      }}
                    >
                      <div style={{ width: '100%', minWidth: 0 }}>
                        {/* Baris 1: Nopol · jam · UPT · total */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <CarOutlined style={{ color: '#1890ff', flexShrink: 0, fontSize: 12 }} />
                          <Tag
                            color="blue"
                            style={{ fontWeight: 700, letterSpacing: 1, minWidth: 86, textAlign: 'center', fontSize: 12, flexShrink: 0, margin: 0 }}
                          >
                            {item.nomor_polisi}
                          </Tag>
                          <Text type="secondary" style={{ fontSize: 11, flexShrink: 0, whiteSpace: 'nowrap' }}>
                            <ClockCircleOutlined style={{ marginRight: 3 }} />{item.paid_on}
                          </Text>
                          {tampilkanUpt && item.upt_nama && (
                            <Tag color="geekblue" style={{ fontSize: 10, borderRadius: 20, flexShrink: 0, margin: 0 }}>
                              {item.upt_nama}
                            </Tag>
                          )}
                          <Text style={{ fontSize: 11, color: '#52c41a', marginLeft: 'auto', flexShrink: 0, fontWeight: 600, whiteSpace: 'nowrap' }}>
                            <DollarOutlined style={{ marginRight: 3 }} />
                            Rp {(item.total || 0).toLocaleString('id-ID')}
                          </Text>
                        </div>
                        {/* Baris 2: Layanan · penetapan_by */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 3, flexWrap: 'wrap' }}>
                          <TagOutlined style={{ color: '#888', fontSize: 10, flexShrink: 0 }} />
                          {(item.layanan || []).length > 0
                            ? (item.layanan || []).map((l, i) => (
                                <Tag key={i} color="cyan" style={{ fontSize: 10, borderRadius: 20, margin: 0, padding: '0 6px' }}>{l}</Tag>
                              ))
                            : <Text type="secondary" style={{ fontSize: 10 }}>—</Text>
                          }
                          {item.penetapan_by && item.penetapan_by !== '-' && (
                            <>
                              <span style={{ color: '#ccc', fontSize: 10, margin: '0 2px' }}>·</span>
                              <UserOutlined style={{ color: '#888', fontSize: 10, flexShrink: 0 }} />
                              <Text type="secondary" style={{ fontSize: 10, whiteSpace: 'nowrap' }}>
                                {item.penetapan_by}
                              </Text>
                            </>
                          )}
                        </div>
                      </div>
                    </List.Item>
                  )}
                />
              )}
            </div>

            {lastUpdate && (
              <Text
                type="secondary"
                style={{ fontSize: 10, display: 'block', marginTop: 6, textAlign: 'right' }}
              >
                Terakhir diperbarui: {lastUpdate.toLocaleTimeString('id-ID')}
              </Text>
            )}
          </Spin>
        )}

        <style>{`
          @keyframes penerimaan-blink {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.3; transform: scale(0.8); }
          }
        `}</style>
      </Card>
    );
  }
}

export default RealtimePenerimaan;
